import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PrimaryButton,
  Select,
  TextInput,
  OutlinedButton,
  CheckBox,
} from "@normasteaching/norma-ui";
import { PropsWithChildren, useState, useEffect, useMemo } from "react";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import {
  CouponType,
  NormasTeachingCouponDTO,
  ApplicableProduct,
} from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { NormasTeachingProductDTO } from "../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import { NormasTeachingBundleDTO } from "../../ProductService/NormasTeachingBundle/useNormasTeachingBundleById.ts";
import { X, Plus, Trash2, Package, Layers } from "lucide-react";
import { matchesItemSearch } from "../../UI/Utils/matchesItemSearch.ts";
import { toDayValue } from "../../UI/Utils/validityDays.ts";

export type CreateNormasTeachingCouponFormValues = {
  couponCode: string;
  value: { amount: number; couponType: CouponType };
  maxNumberOfUses: number;
  validFrom?: string;
  validTo?: string;
  applicableProducts?: ApplicableProduct[];
  isUnlimitedUses?: boolean;
};

const createValidationSchema = (
  existingCoupons: NormasTeachingCouponDTO[],
  isEditMode: boolean,
  initialCouponCode?: string,
): yup.AnyObjectSchema => {
  const baseSchema = yup.object({
    couponCode: yup
      .string()
      .required("Inserire un codice del coupon")
      .test("is-unique", "Questo codice coupon esiste già", function (value) {
        if (!value) return true;
        if (
          isEditMode &&
          initialCouponCode?.toLowerCase() === value.toLowerCase()
        ) {
          return true;
        }
        return !existingCoupons.some(
          (coupon) => coupon.couponCode.toLowerCase() === value.toLowerCase(),
        );
      }),
    value: yup
      .object({
        amount: yup
          .number()
          .required("Inserire un prezzo valido")
          .typeError("Inserire un prezzo valido")
          .test(
            "valid-amount-for-type",
            "Per coupon percentuale, il valore deve essere tra 1 e 100",
            function (value) {
              const { couponType } = this.parent;
              if (couponType === CouponType.Percentage) {
                return value >= 1 && value <= 100;
              }
              return value > 0;
            },
          ),
        couponType: yup
          .string()
          .oneOf(Object.values(CouponType), "Invalid coupon type")
          .required("Inserire una tipologia di coupon valida"),
      })
      .required(),
    isUnlimitedUses: yup.boolean().default(false),
    maxNumberOfUses: yup.number().when("isUnlimitedUses", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required("Inserire il numero massimo di utilizzi")
          .min(1, "Inserire un numero maggiore di 0")
          .typeError("Inserire un numero valido"),
    }),
    validFrom: yup
      .string()
      .nullable()
      .optional()
      .transform((value, originalValue) => {
        return originalValue === "" ? undefined : value;
      }),
    validTo: yup
      .string()
      .nullable()
      .optional()
      .transform((value, originalValue) => {
        return originalValue === "" ? undefined : value;
      })
      .test(
        "is-after-valid-from",
        "La data di fine deve essere successiva alla data di inizio",
        function (value) {
          const { validFrom } = this.parent;
          if (!value || !validFrom) return true;
          return new Date(value) >= new Date(validFrom);
        },
      ),
    applicableProducts: yup
      .array()
      .of(
        yup.object({
          itemType: yup
            .string()
            .oneOf(Object.values(NormasTeachingItemType))
            .required(),
          id: yup.string().required(),
        }),
      )
      .optional(),
  });

  return baseSchema as yup.AnyObjectSchema;
};

function InputWithLabel({
  children,
  label,
}: PropsWithChildren<{ label: string }>) {
  return (
    <div className={"flex flex-col gap-1 flex-1"}>
      <label className={"font-medium"}>{label}</label>
      {children}
    </div>
  );
}

interface ApplicableProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: NormasTeachingProductDTO[];
  bundles: NormasTeachingBundleDTO[];
  selectedItems: ApplicableProduct[];
  onSave: (items: ApplicableProduct[]) => void;
}

function ApplicableProductsModal({
  isOpen,
  onClose,
  products,
  bundles,
  selectedItems,
  onSave,
}: ApplicableProductsModalProps) {
  const [localItems, setLocalItems] = useState<ApplicableProduct[]>([]);
  const [activeTab, setActiveTab] = useState<"products" | "bundles">(
    "products",
  );
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLocalItems(selectedItems);
  }, [selectedItems, isOpen]);

  useEffect(() => {
    if (isOpen) setSearch("");
  }, [isOpen]);

  const visibleProducts = useMemo(
    () =>
      products.filter((product) =>
        matchesItemSearch(search, [
          product._id,
          product.productName,
          product.variantProductName,
        ]),
      ),
    [products, search],
  );

  const visibleBundles = useMemo(
    () =>
      bundles.filter((bundle) =>
        matchesItemSearch(search, [
          bundle._id,
          bundle.bundleName,
          bundle.variantBundleName,
        ]),
      ),
    [bundles, search],
  );

  if (!isOpen) return null;

  const isItemSelected = (id: string, itemType: NormasTeachingItemType) => {
    return localItems.some(
      (item) => item.id === id && item.itemType === itemType,
    );
  };

  const toggleItem = (id: string, itemType: NormasTeachingItemType) => {
    if (isItemSelected(id, itemType)) {
      setLocalItems(
        localItems.filter(
          (item) => !(item.id === id && item.itemType === itemType),
        ),
      );
    } else {
      setLocalItems([...localItems, { id, itemType }]);
    }
  };

  const handleSave = () => {
    onSave(localItems);
    onClose();
  };

  const handleClear = () => {
    setLocalItems([]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-bold">Seleziona Prodotti Applicabili</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActiveTab("products")}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === "products"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Package size={18} />
            Prodotti ({visibleProducts.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("bundles")}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
              activeTab === "bundles"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Layers size={18} />
            Bundle ({visibleBundles.length})
          </button>
        </div>

        <div className="px-6 pt-4">
          <TextInput
            placeholder="Cerca per nome, variante o ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Selected items summary */}
          {localItems.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">
                  {localItems.length} elementi selezionati
                </span>
                <button
                  onClick={handleClear}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Svuota selezione
                </button>
              </div>
            </div>
          )}

          {activeTab === "products" && (
            <div className="space-y-2">
              {visibleProducts.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {search.trim()
                    ? "Nessun prodotto corrisponde alla ricerca"
                    : "Nessun prodotto disponibile"}
                </p>
              ) : (
                visibleProducts.map((product) => (
                  <div
                    key={product._id}
                    onClick={() =>
                      toggleItem(product._id, NormasTeachingItemType.Product)
                    }
                    className={`p-3 border rounded-lg cursor-pointer transition ${
                      isItemSelected(
                        product._id,
                        NormasTeachingItemType.Product,
                      )
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isItemSelected(
                            product._id,
                            NormasTeachingItemType.Product,
                          )
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isItemSelected(
                          product._id,
                          NormasTeachingItemType.Product,
                        ) && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{product.productName}</p>
                        <p className="text-sm text-gray-500">
                          {product.variantProductName} - €
                          {(product.priceInCents.amount / 100).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate">
                          {product._id}
                        </p>
                      </div>
                      <Package size={18} className="text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "bundles" && (
            <div className="space-y-2">
              {visibleBundles.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  {search.trim()
                    ? "Nessun bundle corrisponde alla ricerca"
                    : "Nessun bundle disponibile"}
                </p>
              ) : (
                visibleBundles.map((bundle) => (
                  <div
                    key={bundle._id}
                    onClick={() =>
                      toggleItem(bundle._id, NormasTeachingItemType.Bundle)
                    }
                    className={`p-3 border rounded-lg cursor-pointer transition ${
                      isItemSelected(bundle._id, NormasTeachingItemType.Bundle)
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isItemSelected(
                            bundle._id,
                            NormasTeachingItemType.Bundle,
                          )
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isItemSelected(
                          bundle._id,
                          NormasTeachingItemType.Bundle,
                        ) && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{bundle.bundleName}</p>
                        <p className="text-sm text-gray-500">
                          {bundle.variantBundleName} - €
                          {(bundle.priceInCents.amount / 100).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate">
                          {bundle._id}
                        </p>
                      </div>
                      <Layers size={18} className="text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Annulla
          </button>
          <PrimaryButton label="Salva" onClick={handleSave} />
        </div>
      </div>
    </div>
  );
}

export function CreateNormasTeachingCouponForm({
  coupons,
  creating,
  onSubmit,
  initialCoupon,
  isEditMode = false,
  products,
  bundles,
}: {
  coupons: NormasTeachingCouponDTO[];
  onSubmit: (data: CreateNormasTeachingCouponFormValues) => void;
  creating: boolean;
  initialCoupon?: NormasTeachingCouponDTO;
  isEditMode?: boolean;
  products: NormasTeachingProductDTO[];
  bundles: NormasTeachingBundleDTO[];
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<CreateNormasTeachingCouponFormValues>({
    resolver: yupResolver(
      createValidationSchema(coupons, isEditMode, initialCoupon?.couponCode),
    ),
    defaultValues: initialCoupon
      ? {
          couponCode: initialCoupon.couponCode,
          value: {
            amount:
              initialCoupon.value.couponType === CouponType.Fixed
                ? initialCoupon.value.amount / 100
                : initialCoupon.value.amount,
            couponType: initialCoupon.value.couponType,
          },
          maxNumberOfUses: initialCoupon.maxNumberOfUses,
          validFrom: toDayValue(initialCoupon.validFrom),
          validTo: toDayValue(initialCoupon.validTo),
          applicableProducts: initialCoupon.applicableProducts || undefined,
          isUnlimitedUses: initialCoupon.isUnlimitedUses ?? false,
        }
      : {
          applicableProducts: undefined,
          isUnlimitedUses: false,
        },
  });

  const validFrom = watch("validFrom");
  const applicableProducts = watch("applicableProducts");
  const isUnlimitedUses = watch("isUnlimitedUses");

  const getItemName = (item: ApplicableProduct): string => {
    if (item.itemType === NormasTeachingItemType.Product) {
      const product = products.find((p) => p._id === item.id);
      return product
        ? `${product.productName} - ${product.variantProductName}`
        : "Prodotto sconosciuto";
    } else {
      const bundle = bundles.find((b) => b._id === item.id);
      return bundle
        ? `${bundle.bundleName} - ${bundle.variantBundleName}`
        : "Bundle sconosciuto";
    }
  };

  const handleRemoveItem = (id: string, itemType: NormasTeachingItemType) => {
    const currentItems = applicableProducts || [];
    setValue(
      "applicableProducts",
      currentItems.filter(
        (item) => !(item.id === id && item.itemType === itemType),
      ),
    );
  };

  const handleClearAll = () => {
    setValue("applicableProducts", undefined);
  };

  const onSubmitInternal = async (
    data: CreateNormasTeachingCouponFormValues,
  ) => {
    const processedData = {
      ...data,
      validFrom: data.validFrom || undefined,
      validTo: data.validTo || undefined,
      applicableProducts:
        data.applicableProducts && data.applicableProducts.length > 0
          ? data.applicableProducts
          : undefined,
      maxNumberOfUses: data.isUnlimitedUses ? 0 : data.maxNumberOfUses,
    };
    onSubmit(processedData);
  };

  return (
    <form className={"flex flex-col gap-4"}>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Codice Coupon"}>
          <TextInput
            {...register("couponCode")}
            errorMessage={errors?.couponCode?.message}
            placeholder="es. SCONTO2025"
          />
        </InputWithLabel>

        <InputWithLabel label={"Valore del coupon"}>
          <TextInput
            type="number"
            step="0.01"
            min={0}
            {...register("value.amount")}
            errorMessage={errors?.value?.amount?.message}
            placeholder="es. 10 o 20"
          />
        </InputWithLabel>

        <InputWithLabel label={"Tipologia Coupon"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  errorMessage={errors?.value?.couponType?.message}
                  value={field.value}
                  onChange={field.onChange}
                  options={Object.entries(CouponType).map(([key, value]) => {
                    return {
                      name: key,
                      id: value,
                    };
                  })}
                />
              );
            }}
            name={"value.couponType"}
            control={control}
          />
        </InputWithLabel>
      </div>

      <div className={"flex flex-col md:flex-row gap-4 items-end"}>
        <div className="flex-1">
          <Controller
            render={({ field }) => {
              return (
                <CheckBox
                  label={
                    <span className="font-medium">Utilizzi illimitati</span>
                  }
                  value={field.value ?? false}
                  onChange={(checked) => {
                    field.onChange(checked);
                    if (checked) {
                      setValue("maxNumberOfUses", 0);
                    }
                  }}
                />
              );
            }}
            name={"isUnlimitedUses"}
            control={control}
          />
        </div>

        {!isUnlimitedUses && (
          <InputWithLabel label="Numero massimo di utilizzi">
            <TextInput
              type="number"
              min={1}
              {...register("maxNumberOfUses")}
              errorMessage={errors?.maxNumberOfUses?.message}
              placeholder="es. 100"
            />
          </InputWithLabel>
        )}
      </div>

      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label="Valido da (opzionale)">
          <input
            type="date"
            {...register("validFrom")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors?.validFrom && (
            <span className="text-sm text-red-600">
              {errors.validFrom.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite iniziale
          </span>
        </InputWithLabel>

        <InputWithLabel label="Valido fino a (opzionale)">
          <input
            type="date"
            min={validFrom}
            {...register("validTo")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {errors?.validTo && (
            <span className="text-sm text-red-600">
              {errors.validTo.message}
            </span>
          )}
          <span className="text-xs text-gray-500">
            Lascia vuoto per nessun limite finale
          </span>
        </InputWithLabel>
      </div>

      {/* Applicable Products Section */}
      <div className="border rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <div>
            <label className="font-medium text-gray-800">
              Prodotti Applicabili
            </label>
            <p className="text-sm text-gray-600">
              Seleziona i prodotti/bundle a cui questo coupon è applicabile.
              Lascia vuoto per applicarlo a tutti.
            </p>
          </div>
          <OutlinedButton
            iconLeft={<Plus size={18} />}
            label="Seleziona"
            onClick={() => setIsModalOpen(true)}
          />
        </div>

        {applicableProducts && applicableProducts.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {applicableProducts.length} elementi selezionati
              </span>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-sm text-red-600 hover:text-red-800"
              >
                Rimuovi tutti
              </button>
            </div>
            <div className="bg-white rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
              {applicableProducts.map((item) => (
                <div
                  key={`${item.itemType}-${item.id}`}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <div className="flex items-center gap-2">
                    {item.itemType === NormasTeachingItemType.Product ? (
                      <Package size={16} className="text-blue-500" />
                    ) : (
                      <Layers size={16} className="text-purple-500" />
                    )}
                    <span className="text-sm">{getItemName(item)}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        item.itemType === NormasTeachingItemType.Product
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {item.itemType === NormasTeachingItemType.Product
                        ? "Prodotto"
                        : "Bundle"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id, item.itemType)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500 text-sm">
              Nessun prodotto selezionato. Il coupon sarà applicabile a tutti i
              prodotti e bundle.
            </p>
          </div>
        )}
      </div>

      <PrimaryButton
        loading={creating}
        label={isEditMode ? "Aggiorna Coupon" : "Crea Coupon"}
        onClick={handleSubmit(onSubmitInternal)}
      />

      <ApplicableProductsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        products={products}
        bundles={bundles}
        selectedItems={applicableProducts || []}
        onSave={(items) =>
          setValue("applicableProducts", items.length > 0 ? items : undefined)
        }
      />
    </form>
  );
}
